<template>
  <Beacon v-if="$route.path === keycloakBeaconPage" />

  <LayoutA v-else>
    <template slot="nav-left">
      <a v-if="loggedIn" class="navbar-item" @click="showProfileDialog = true">
        {{ $t("menu.profile") | titlecase }}
      </a>
    </template>
    <template slot="nav-right">
      <LanguageMenu />
      <a v-if="tutorialVideo" class="navbar-item" @click="showTutorial = true">
        {{ $t("menu.tutorial") | titlecase }}
      </a>
      <router-link to="/impressum" class="navbar-item">
        {{ $t("menu.imprint") | titlecase }}
      </router-link>
      <a v-if="loggedIn" class="navbar-item" @click="logout">
        {{ $t("menu.logout") | titlecase }}
      </a>
      <router-link
        v-if="requiresNoLogin && !loggedIn"
        class="navbar-item"
        to="/"
      >
        {{ $t("menu.login") | titlecase }}
      </router-link>
    </template>

    <template slot="welcome">
      {{
        $store.getters.userName
          ? $t("message.welcome.personal", { name: $store.getters.userName })
          : $t("message.welcome.generic")
      }}
    </template>

    <template slot="content">
      <IdleRedirect>
        <KeycloakIFrame
          v-if="!skipAuthentication"
          v-show="!requiresNoLogin"
          :redirect-uri="keycloakRedirectURI"
          :locale="$i18n.i18next.language"
          class="spread-iframe"
        />
      </IdleRedirect>
      <NavBar v-if="loggedIn" />
      <router-view v-if="loggedIn || requiresNoLogin" />
    </template>

    <template slot="footer">
      <ContactInfo v-bind="contact" />
    </template>

    <template slot="dialogs">
      <ProfileDialog
        v-if="showProfileDialog"
        @close="showProfileDialog = false"
      />
      <ModalDialog
        v-if="showTutorial"
        :title="$t('modal.tutorial.title') | titlecase"
        @close="showTutorial = false"
      >
        <video controls>
          <source :src="tutorialVideo" type="video/mp4" />
        </video>
      </ModalDialog>
    </template>
  </LayoutA>
</template>

<script>
import { mapState, mapGetters } from "vuex";

import { LayoutA, ContactInfo, ModalDialog } from "@apphq/vue-base-components";

import { Beacon, KeycloakIFrame } from "@apphq/vue-keycloak-iframe";
import { IdleRedirect } from "@apphq/idle-timer";
import { ProfileDialog } from "@apphq/user-profile/src";

import LanguageMenu from "@/i18n/LanguageMenu.vue";
import NavBar from "@/components/NavBar.vue";

import { authStore, profileStore } from "@/compose";

export default {
  name: "RvkApp",
  components: {
    LayoutA,
    KeycloakIFrame,
    Beacon,
    IdleRedirect,
    LanguageMenu,
    NavBar,
    ModalDialog,
    ContactInfo,
    ProfileDialog
  },
  provide: {
    authStore,
    profileStore
  },
  data() {
    return {
      keycloakBeaconPage: "/keycloak-logged-in",
      keycloakRedirectURI:
        window.location.protocol +
        "//" +
        window.location.host +
        process.env.BASE_URL +
        "keycloak-logged-in",
      showProfileDialog: false,
      showTutorial: false
    };
  },
  computed: {
    requiresNoLogin() {
      return Boolean(this.$route.meta.requiresNoLogin);
    },
    skipAuthentication() {
      return Boolean(this.$route.meta.skipAuthentication);
    },
    language() {
      return this.$i18n.i18nLoadedAt && this.$i18n.i18next.language;
    },
    tutorialVideo() {
      if (!this.loggedIn || this.language != "de") {
        return;
      }
      if (authStore.hasRole("rvkRhenusSuperUser")) {
        return "/vid/RVA_Tutorial_SuperUser.mp4";
      } else if (authStore.hasRole("rvkRhenusAdmin")) {
        return "/vid/RVA_Tutorial_MitarbeiterIn.mp4";
      } else {
        return "/vid/RVA_Tutorial_Kunde.mp4";
      }
    },
    loggedIn() {
      return authStore.loggedIn;
    },
    loggedOut() {
      return authStore.loggedOut;
    },
    ...mapGetters({
      contact: "contactInfo"
    })
  },
  watch: {
    loggedIn(newValue, oldValue) {
      if (newValue && !oldValue) {
        this.$store.dispatch("loadContactInfo");
        profileStore.loadData();
      }
    },
    loggedOut(newValue, oldValue) {
      if (newValue && !oldValue) {
        this.$store.commit("reset");
        profileStore.reset();
      }
    }
  },
  methods: {
    logout() {
      authStore.logout();
    }
  },
  metaInfo() {
    return {
      title: this.$t("meta.title"),
      meta: [
        {
          vmid: "description",
          name: "description",
          content: this.$t("meta.description")
        }
      ]
    };
  }
};
</script>

<style lang="scss">
@import "~@apphq/rhenus-design-base/sass/variables.scss";

main {
  margin: 1.5rem;
}

.spread-iframe iframe {
  width: 100%;
  height: 100vh;
}

.is-breakable {
  overflow-wrap: break-word;
  word-break: break-all;
}
</style>
